import { Edit } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Button, Chip, Grid, IconButton, InputBase, Switch, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addReportRecipient, deleteReportRecipient, updateActiveReport, deleteReportSchedule, updateActiveStep, clearFormValues } from '../../../../redux/slices/reports';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import { SCHEDULED_REPORTS } from '../../../../reusable-components/datagrid/reportsColumns';

ReportScheduleTable.propTypes = {
  setRow: PropTypes.func,
  setOpen: PropTypes.func,
  setEdit: PropTypes.func,
};

export default function ReportScheduleTable({ setRow, setOpen, setEdit }) {
  const dispatch = useDispatch();
  const { reportSchedules, reportRecipients } = useSelector((state) => state.reports?.data);
  const [showBox, setShowBox] = useState(0);
  const [emailInputs, setEmailInputs] = useState({});

  const tableToolbar = useCallback(() => <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
    <Button
      variant="outlined"
      sx={{ mr: 1 }}
      size="small"
      color="secondary"
      onClick={() => {
        setRow(null);
        dispatch(clearFormValues());
        dispatch(updateActiveStep(0));
        setEdit(false);
        setOpen(true);
      }}
    >
      New Report
    </Button>
  </Grid>, []);

  const recipients = {
    field: 'recipients',
    headerName: 'Recipients',
    type: 'customText',
    minWidth: 250,
    renderCell: (params) => {
      const recipientMap = Object.fromEntries((params.row?.recipients || []).map(r => [r.label, r]));

      const userRecipients = reportRecipients
        .filter(user => recipientMap[user.email])
        .map(user => ({
          ...user,
          recipientId: recipientMap[user.email]?.value
        }));

      const nonUserRecipients = (params.row?.recipients || [])
        .filter(r => !reportRecipients.some(user => user.email === r.label))
        .map(r => ({
          firstName: r.label,
          email: r.label,
          recipientId: r.value,
        }));

      const matchedUsers = [...userRecipients, ...nonUserRecipients];


      return <Box key={`${params.id}${showBox}`} sx={{ py: 0.5 }} display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
        {matchedUsers.map(r =>
          <Tooltip key={r.id} title={r.email}>
            <Chip
              color="primary"
              label={r.firstName}
              size="small"
              onDelete={() => dispatch(deleteReportRecipient(r.recipientId, params.id))} />
          </Tooltip>
        )}
        {showBox !== params.id ?
          <IconButton key={`${params.id}${showBox}`}
            size="small"
            sx={{
              width: 30,
              height: 30,
              p: 0,
              alignSelf: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={() => setShowBox(params.row.id)}
          >
            <AddCircleOutlineIcon sx={{ color: "primary.main", fontSize: 18 }} />
          </IconButton>
          :
          <Tooltip title="Add Recipient">
            <Autocomplete
              disableClearable
              freeSolo
              options={reportRecipients.map((u) => u.email) || []}
              value={emailInputs[params.row.id] || ''}
              onInputChange={(_, val) => {
                setEmailInputs((inputs) => ({ ...inputs, [params.row.id]: val || '' }));
              }}
              onChange={(_, val) => {
                setEmailInputs((inputs) => ({
                  ...inputs,
                  [params.row.id]: val || '',
                }));
              }}
              renderInput={(params2) => (
                <InputBase
                  {...params2.InputProps}
                  endAdornment={
                    <IconButton
                      variant="contained"
                      edge="end"
                      sx={{
                        color: "primary.main",
                        height: 24,
                        width: 24,
                        p: 0,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={async (e) => {
                        e.preventDefault();
                        await dispatch(addReportRecipient(emailInputs[params.row?.id], params.row?.id));
                        setShowBox(0);
                        setEmailInputs((inputs) => ({
                          ...inputs,
                          [params.row.id]: '',
                        }));
                      }}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  }
                  inputProps={{
                    ...params2.inputProps,
                    style: {
                      width: 'auto',
                      padding: 0,
                      fontSize: '0.75rem',
                      height: 24,
                      lineHeight: '24px',
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  sx={{
                    minWidth: '30px',
                    paddingX: 1,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: '9999px',
                    backgroundColor: 'background.paper',
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onKeyDown={async (e) => {
                    if (
                      (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
                      document.activeElement === e.target
                    ) {
                      e.stopPropagation();
                    }
                  }}
                />
              )}
            />
          </Tooltip>
        }
      </Box >
    }
  }

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    type: 'customText',
    minWidth: 170,
    renderCell: (params) => (
      <Box>
        <Switch checked={params.row.active} onChange={(e) => dispatch(updateActiveReport(params.row.id, e.target.checked))} />
        <IconButton
          onClick={() => {
            setRow(params.row);
            setEdit(true);
            setOpen(true);
          }}
        ><Edit color="secondary" />
        </IconButton>
        <ConfirmDialog
          icon="delete-outline"
          title="Delete"
          color="error"
          content={`Are you sure you want to delete ${params.row.name} (${params.row.reportName})?`}
          actionButton="Delete"
          action={() => dispatch(deleteReportSchedule(params.row.id))}
        />
      </Box>
    ),
  }

  const scheduleColumns = [...SCHEDULED_REPORTS, recipients, actions];

  return (<CustomDataGrid
    forceReRender={[showBox, emailInputs]}
    CustomLeftToolbar={tableToolbar}
    gridId="scheduled-reports"
    boxSX={{ height: 'calc(100vh - 310px)', width: '100%' }}
    data={reportSchedules ?? []}
    gridColumns={scheduleColumns}
    isLoading={false}
    noRowsComponent={
      <Typography>Click to add a{' '}
        <Button
          variant="text"
          sx={{
            mr: 1,
            textDecoration: 'underline',
            '&:hover': { textDecoration: 'underline' }
          }}
          size="small"
          color="secondary"
          onClick={() => {
            setRow(null);
            dispatch(clearFormValues());
            dispatch(updateActiveStep(0));
            setOpen(true);
          }}
        >
          scheduled Report
        </Button>
      </Typography>
    }
  />);
}